"use strict";

// Copyright 2012-2020 The MathWorks, Inc.
define(['dojo/_base/array', 'dojo/_base/declare', 'dojo/_base/Deferred', 'dojo/_base/lang', 'mw-remote/RemoteEndPointsManager', 'mw-remote/defaultMessageMapping', './Transaction'], function (array, declare, Deferred, lang, RemoteEndPointsManager, defaultMessageMapping, Transaction) {
  /**
   * The RequestManager is responsible for sending requests to the server. It takes a message and
   * bundles it up with and extra messages that need to be sent along with the main request. It
   * maintains a mapping of request types to extra types which need to be added. It also has a
   * registry of default factory functions which it uses to construct the extra messages
   * (registered with "registerDefaultMethodFactory").
   */
  return declare([], {
    defaultMapping: defaultMessageMapping,
    constructor: function constructor(args) {
      args = args || {};
      this.defaultFactories = args.defaultFactories || {};
      this.mockHandlers = args.mockHandlers || {};
      this.mapping = args.mapping || this.defaultMapping;
      this.responseManager = args.responseManager;
    },

    /**
     * Register a factor function which constructs messages of the specified type.
     * @param type Type of message that the registered function constructs.
     * @param factoryFunction Function object to call. This will be passed the transaction
     * context which was passed to sendMessage. This function must return an object or array of
     * objects with the specified message schema.
     * @param scope Optional scope to bind the function to.
     */
    registerDefaultMessageFactory: function registerDefaultMessageFactory(type, factoryFunction, scope) {
      if (scope) {
        factoryFunction = lang.hitch(scope, factoryFunction);
      }

      this.defaultFactories[type] = factoryFunction;
    },

    /**
     * Deregisters a message factory function.
     * @param type Type of message.
     */
    deregisterDefaultMessageFactory: function deregisterDefaultMessageFactory(type) {
      delete this.defaultFactories[type];
    },

    /**
     * Registers a function that will generate a mock response for the given message
     * type. If a message in the transaction matches the type, then the response
     * generator function gets called, and the message type is not sent to the remote
     * endpoint.
     *
     * @param type The type of the request message for which we'll generate a mock response.
     * @param mockResponseGenerator The response generator function. This will be called in the
     * context of the request manager, and it will be passed the message array for the
     * registered type and a copy of the transaction object created for the request. The result
     * must be a Deferred which will send the transaction on to the responseManager when it's
     * done.
     */
    registerMockResponseGenerator: function registerMockResponseGenerator(type, mockResponseGenerator, scope) {
      var that = this;
      mockResponseGenerator = lang.hitch(scope || this, mockResponseGenerator);

      this.mockHandlers[type] = function (request, transaction) {
        var deferred;
        var transactionCopy = Transaction.construct(transaction);
        deferred = mockResponseGenerator(request, transactionCopy);

        if (!deferred) {
          deferred = new Deferred();
          deferred.callback();
        }

        deferred.addCallback(function () {
          that.responseManager._processResult(transactionCopy);
        });
      };
    },
    deregisterMockResponseGenerator: function deregisterMockResponseGenerator(type) {
      delete this.mockHandlers[type];
    },

    /**
     * Send a message to the server. This will automatically create any extra messages which
     * need to be packaged along with the initial message(s).
     * @param message One or more messages defined in an Object:
     *   \{messageType: \{param1:"1",param2:[1,2,3]\}\}, or
     *   \{messageType: \{param1:"1",param2:[1,2,3]\},
     *   type2: \{param1:"1",param2:[1,2,3]\}\}, or
     *   \{messageType: [\{param1:"1",param2:[1,2,3]\}]\}
     * @param context Optional. Any arbitrary context to associate with the message. This data
     * is not sent to the server, but it is available to the default factory function (s) and
     * the response/fault handlers. This can be used for correlation between request/response
     * within a client of the remote layer.
     * @param classifier Optional. A string classifier for the message. This will be used when
     * there is a response/fault/mock registered with a classifier. This classifier applies to
     * all types in the message parameter.
     */
    sendMessage: function sendMessage(message, context, classifier) {
      var transactions = {};
      var mockTransactions = {};
      var endPointNames;
      var i;
      var endPointName;
      context = context || {}; // Construct and fill the transaction according to the endPoint Types

      this._createAndFillTransactions(message, context, classifier, transactions, mockTransactions);

      endPointNames = Object.keys(transactions);

      for (i = 0; i < endPointNames.length; i = i + 1) {
        endPointName = endPointNames[i];

        this._callMocks(mockTransactions[endPointName]);

        transactions[endPointName].send();
        this.responseManager.handleTransaction(transactions[endPointName]);
      }
    },

    /**
     * This function returns a new transaction.
     * @param endPointName
     * @param context
     * @return {*} transaction.
     * @private
     */
    _getTransaction: function _getTransaction(endPointName, context) {
      var transaction = Transaction.construct({
        context: context,
        computeToken: this.computeToken,
        endPointName: endPointName
      });
      return transaction;
    },

    /**
     * This function returns mock transaction.
     * @param endPointName
     * @param context
     * @param uuid
     * @return {*} mockTransaction.
     * @private
     */
    _getMockTransaction: function _getMockTransaction(endPointName, context, uuid) {
      var mockTransaction = Transaction.construct({
        context: context,
        computeToken: this.computeToken,
        endPointName: endPointName,
        uuid: uuid
      });
      return mockTransaction;
    },

    /**
     * This function loops through the messages passed. For each message type, it checks the
     * default message mappings and adds relevant messages to transaction. Transactions are
     * constructed per endPoint. Hence this function fills transactions object with transaction
     * per endPoint and messages mapped to each transaction.
     * @param message
     * @param context
     * @param classifier
     * @param transactions
     * @param mockTransactions
     * @private
     */
    _createAndFillTransactions: function _createAndFillTransactions(message, context, classifier, transactions, mockTransactions) {
      var type, classifiedType;

      var addToTransaction = function addToTransaction(extraMessageType) {
        var newMessage, typeParts, newMessageType, newClassifier, extraMessageEndPointName, transaction, mockTrans;
        extraMessageEndPointName = RemoteEndPointsManager.getEndPointForMessageType(extraMessageType);
        transaction = transactions[extraMessageEndPointName];
        mockTrans = mockTransactions[extraMessageEndPointName];

        if (!transaction) {
          transaction = this._getTransaction(extraMessageEndPointName, context);
          mockTrans = this._getMockTransaction(extraMessageEndPointName, context, transaction.uuid);
          transactions[extraMessageEndPointName] = transaction;
          mockTransactions[extraMessageEndPointName] = mockTrans;
        }

        if (this.defaultFactories[extraMessageType]) {
          newMessage = this.defaultFactories[extraMessageType](transaction.context);

          if (!newMessage) {
            // skip this message if it is null or undefined
            return;
          } // parse the message type for a classifier


          typeParts = extraMessageType.split(':');
          newMessageType = typeParts[0];
          newClassifier = typeParts.slice(1, typeParts.length).join(':'); // if there is a mock handler, put the message into a different transaction

          if (this.mockHandlers[extraMessageType]) {
            mockTrans.addRequestMessage(newMessageType, newMessage, newClassifier);
          } else {
            transaction.addRequestMessage(newMessageType, newMessage, newClassifier);
            mockTrans.addRequestMessage(newMessageType, newMessage, newClassifier);
          }
        }
      };

      var mockTransaction, transaction;

      for (type in message) {
        if (message.hasOwnProperty(type)) {
          var endPointName = RemoteEndPointsManager.getEndPointForMessageType(type);
          mockTransaction = mockTransactions[endPointName];
          transaction = transactions[endPointName];

          if (!transactions[endPointName]) {
            transaction = this._getTransaction(endPointName, context);
            mockTransaction = this._getMockTransaction(endPointName, context, transaction.uuid);
            transactions[endPointName] = transaction;
            mockTransactions[endPointName] = mockTransaction;
          }

          classifiedType = [type, classifier].join(':'); // if there is a mock handler, put the message into a different transaction

          if (this.mockHandlers[type] || this.mockHandlers[classifiedType]) {
            mockTransaction.addRequestMessage(type, message[type], classifier);
          } else {
            transaction.addRequestMessage(type, message[type], classifier);
            mockTransaction.addRequestMessage(type, message[type], classifier);
          }

          if (this.mapping[classifiedType]) {
            this.mapping[classifiedType].forEach(addToTransaction, this);
          } else if (this.mapping[type]) {
            this.mapping[type].forEach(addToTransaction, this);
          }
        }
      }
    },
    _callMocks: function _callMocks(mockTransaction) {
      var clone, type;
      var context = mockTransaction.context; // Don't clone the context

      delete mockTransaction.context;

      var callMocksWithClassifiers = function callMocksWithClassifiers(type, message) {
        var typeWithClassifier;
        var classifier = clone.getMessageClassifier(message);

        if (classifier) {
          typeWithClassifier = [type, classifier].join(':');

          if (this.mockHandlers[typeWithClassifier]) {
            this.mockHandlers[typeWithClassifier]([message], clone);
          }
        }
      }; // loop through and handle the mock request messages.


      for (type in mockTransaction.requestMessages) {
        if (mockTransaction.requestMessages.hasOwnProperty(type)) {
          // use mixin rather than clone to avoid a deep copy of the iframe proxy client
          // references
          clone = lang.mixin({}, mockTransaction);
          clone.context = context;

          if (this.mockHandlers.hasOwnProperty(type)) {
            // handle with the mock
            this.mockHandlers[type](clone.requestMessages[type], clone);
          }

          if (Array.isArray(clone.requestMessages[type])) {
            clone.requestMessages[type].forEach(lang.hitch(this, callMocksWithClassifiers, type));
          } else {
            callMocksWithClassifiers(type, clone.requestMessages[type]);
          }
        }
      }

      if (this.mockHandlers['*']) {
        this.mockHandlers['*'](type, clone.requestMessages[type], clone);
      }
    }
  });
});
//# sourceMappingURL=RequestManager.js.map
